// v1.34+: 自动拓客配置页
// 路径: /auto-prospect
// 功能:
//   - 编辑 PROSPECT_SEEDS (手动种子列表) + PROSPECT_QUERIES (自动找客户条件)
//   - 实时保存到 D1 (不需 wrangler secret)
//   - "立即跑一次" 按钮 (不等到周一)
//   - 显示当前 env 配置作参考

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { autoProspectApi } from '../lib/api';

export const Route = createFileRoute('/auto-prospect')({
  component: AutoProspectPage,
});

function AutoProspectPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['auto-prospect-config'],
    queryFn: () => autoProspectApi.get(),
  });

  const [seeds, setSeeds] = useState('');
  const [queries, setQueries] = useState('');
  const [savedSeeds, setSavedSeeds] = useState('');
  const [savedQueries, setSavedQueries] = useState('');
  const [toast, setToast] = useState<{
    kind: 'success' | 'error' | 'info';
    msg: string;
  } | null>(null);

  useEffect(() => {
    if (data) {
      // 优先用 D1 配置, 没有则用 env fallback
      const initialSeeds = data.seeds || data.envFallback.seeds || '';
      const initialQueries = data.queries || data.envFallback.queries || '';
      setSeeds(initialSeeds);
      setQueries(initialQueries);
      setSavedSeeds(initialSeeds);
      setSavedQueries(initialQueries);
    }
  }, [data]);

  const saveMut = useMutation({
    mutationFn: () => autoProspectApi.update({ seeds, queries }),
    onSuccess: (res) => {
      setSavedSeeds(seeds);
      setSavedQueries(queries);
      qc.invalidateQueries({ queryKey: ['auto-prospect-config'] });
      setToast({
        kind: 'success',
        msg: `已保存 (${new Date(res.updatedAt).toLocaleString()})`,
      });
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  const runMut = useMutation({
    mutationFn: () => autoProspectApi.runNow(),
    onSuccess: (res) => {
      if (res.ok) {
        setToast({
          kind: 'success',
          msg: `✅ 跑完! 自动找到 ${res.autoFound} / 导入 ${res.imported} / 跳过 ${res.skipped} / 失败 ${res.failed}`,
        });
        qc.invalidateQueries({ queryKey: ['customers'] });
      } else {
        setToast({ kind: 'error', msg: res.error ?? '运行失败' });
      }
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  const dirty = seeds !== savedSeeds || queries !== savedQueries;

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-8 bg-surface-2 rounded w-1/3" />
        <div className="h-48 bg-surface-2 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink flex items-center gap-2">🤖 自动拓客配置</h1>
          <p className="text-sm text-ink-3 mt-1">
            每周一 CST 10:00 自动跑 (UTC 2:00)。配置好后不需要任何手动操作。
          </p>
        </div>
        <Link to="/onboarding" className="text-xs text-ink-3 hover:text-ink underline">
          ← 回到引导
        </Link>
      </div>

      {toast && (
        <Alert variant={toast.kind} onDismiss={() => setToast(null)}>
          {toast.msg}
        </Alert>
      )}

      {/* SEEDS 手动种子 */}
      <Card padding="md">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1 h-4 bg-accent rounded-sm" />
          <h2 className="font-semibold text-sm">手动种子 (PROSPECT_SEEDS)</h2>
          <span className="text-xs text-ink-3">你已知的公司, 每周 cron 必跑</span>
        </div>
        <p className="text-xs text-ink-3 mb-3">
          格式: <code className="bg-surface-2 px-1 rounded">公司名|国家|可选域名</code>， 多个用{' '}
          <code className="bg-surface-2 px-1 rounded">,</code> 分隔
        </p>
        <textarea
          value={seeds}
          onChange={(e) => setSeeds(e.target.value)}
          rows={5}
          placeholder="Tanaka Trading Co.|JP|tanaka-trading.co.jp&#10;Mitsui & Co Europe|DE&#10;Toyota Tsusho|UK|toyota-tsusho.com"
          className="w-full px-3 py-2 text-sm font-mono bg-surface border border-line rounded focus:border-accent focus:outline-none"
        />
        <p className="text-xs text-ink-3 mt-2">
          当前 {seeds.split(/[,\n]/).filter((s) => s.trim()).length} 个种子
        </p>
      </Card>

      {/* QUERIES 自动找客户 */}
      <Card padding="md">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1 h-4 bg-accent rounded-sm" />
          <h2 className="font-semibold text-sm">自动找客户 (PROSPECT_QUERIES)</h2>
          <span className="text-xs text-ink-3">按行业+国家+关键词自动搜 Google 找新公司</span>
        </div>
        <p className="text-xs text-ink-3 mb-3">
          格式: <code className="bg-surface-2 px-1 rounded">行业|国家|关键词</code>， 多个用{' '}
          <code className="bg-surface-2 px-1 rounded">,</code> 分隔
        </p>
        <textarea
          value={queries}
          onChange={(e) => setQueries(e.target.value)}
          rows={4}
          placeholder="toys|JP|OEM manufacturer&#10;electronics|DE|supplier&#10;machinery|US|distributor"
          className="w-full px-3 py-2 text-sm font-mono bg-surface border border-line rounded focus:border-accent focus:outline-none"
        />
        <p className="text-xs text-ink-3 mt-2">
          当前 {queries.split(/[,\n]/).filter((s) => s.trim()).length} 个查询
        </p>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          onClick={() => saveMut.mutate()}
          loading={saveMut.isPending}
          disabled={!dirty}
        >
          {saveMut.isPending ? '保存中' : '💾 保存配置'}
        </Button>
        <Button variant="secondary" onClick={() => runMut.mutate()} loading={runMut.isPending}>
          {runMut.isPending ? '运行中 (可能 30-60s)' : '▶️ 立即跑一次'}
        </Button>
        {data?.updatedAt && (
          <span className="text-xs text-ink-3 self-center">
            上次保存: {new Date(data.updatedAt).toLocaleString()}
          </span>
        )}
      </div>

      {/* env fallback reference */}
      {data?.envFallback && (data.envFallback.seeds || data.envFallback.queries) && (
        <Card padding="sm" className="bg-surface-2">
          <details>
            <summary className="text-xs text-ink-3 cursor-pointer">
              📋 env 备份配置 (供参考, UI 配置优先)
            </summary>
            <div className="mt-2 space-y-2 text-xs font-mono">
              {data.envFallback.seeds && (
                <div>
                  <div className="text-ink-2 font-sans mb-1">PROSPECT_SEEDS (env):</div>
                  <pre className="bg-surface p-2 rounded whitespace-pre-wrap break-all">
                    {data.envFallback.seeds}
                  </pre>
                </div>
              )}
              {data.envFallback.queries && (
                <div>
                  <div className="text-ink-2 font-sans mb-1">PROSPECT_QUERIES (env):</div>
                  <pre className="bg-surface p-2 rounded whitespace-pre-wrap break-all">
                    {data.envFallback.queries}
                  </pre>
                </div>
              )}
            </div>
          </details>
        </Card>
      )}

      {/* Help */}
      <Card variant="bordered" className="bg-accent/5 border-accent/20">
        <div className="flex items-start gap-3">
          <span className="text-xl">💡</span>
          <div className="text-sm text-ink-2 space-y-2">
            <p className="font-medium text-ink">工作原理</p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>配置保存到 D1 (app_config 表), 不需要 wrangler secret</li>
              <li>每周一 CST 10:00 cron 触发 runWeeklyProspecting</li>
              <li>先读 PROSPECT_QUERIES → 调 prospect_dork_search → 找候选公司</li>
              <li>合并种子 + 候选 → prospect_enrich (背调) → 写入 customer 表</li>
              <li>飞书推周报 (种子数/找到数/导入数/制裁命中)</li>
              <li>你点 "立即跑一次" 可不等到周一,30-60s 内出结果</li>
            </ol>
            <p className="text-xs text-ink-3 mt-2">
              ⚠️ Google 搜索从 Cloudflare Worker IP 出去会有限流 (429), 候选数会少。 建议混合用
              PROSPECT_SEEDS (手动补) + PROSPECT_QUERIES (自动找)。
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
